/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FileInputWidget", ["WidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Upload widget.
   * @class FileInputWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   * @publicdoc Widgets
   */
  cls.FileInputWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.FileInputWidget.prototype */ {
      __name: "FileInputWidget",

      $static: /** @lends classes.FileInputWidget */ {
        statusChangedEvent: "statusChanged",
        fileSelectionChangedEvent: "fileSelectionChanged",
        progressChangedEvent: "progressChanged"
      },

      _allowMultipleFiles: false,
      /** @type {?string} */
      _rawCaption: null,

      _files: null,

      /**
       * @type {HTMLElement}
       */
      _hiddenInput: null,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._hiddenInput = this._element.querySelector("input");
        cls.DOMElementHelper.on(this._element, "drop.FileInputWidget", this._onDrop.bind(this));
        cls.DOMElementHelper.on(this._element, "dragenter.FileInputWidget", this._onDragEnter.bind(this));
        cls.DOMElementHelper.on(this._element, "dragleave.FileInputWidget", this._onDragLeave.bind(this));
        cls.DOMElementHelper.on(this._element, "dragover.FileInputWidget", this._onDragOver.bind(this));
        cls.DOMElementHelper.on(this._element, "mouseover.FileInputWidget", this._onMouseOver.bind(this));
        cls.DOMElementHelper.on(this._element, "mouseout.FileInputWidget", this._onMouseOut.bind(this));
        cls.DOMElementHelper.on(this._hiddenInput, "change.FileInputWidget", this._onFileChanged.bind(this));
        this.setCaption();
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._hiddenInput = null;
        cls.DOMElementHelper.off(this._element, "drop.FileInputWidget");
        cls.DOMElementHelper.off(this._element, "dragenter.FileInputWidget");
        cls.DOMElementHelper.off(this._element, "dragleave.FileInputWidget");
        cls.DOMElementHelper.off(this._element, "dragover.FileInputWidget");
        cls.DOMElementHelper.off(this._element, "mouseover.FileInputWidget");
        cls.DOMElementHelper.off(this._element, "mouseout.FileInputWidget");
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._hiddenInput.click();
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * Handler when a file is dropped on the zone
       * @param {MouseEvent} event
       * @private
       */
      _onDrop: function (event) {
        this.removeClass("dropping");
        event.stopPropagation();
        event.stopImmediatePropagation();
        cls.EventHelper.preventCancelableDefault(event);
        this._files = event.dataTransfer.files;
        this._onFileChanged();
      },

      /**
       * Handler on drag enter the zone
       * @param {Object} event - DOM event
       * @private
       */
      _onDragEnter: function (event) {
        this.addClasses("dropping");
      },

      /**
       * Handler on drag leave the zone
       * @param {Object} event - DOM event
       * @private
       */
      _onDragLeave: function (event) {
        this.removeClass("dropping");
      },

      /**
       * Handler while dragging over the zone
       * @param {MouseEvent} event
       * @private
       */
      _onDragOver: function (event) {
        this.addClasses("dropping");
        event.stopPropagation();
        event.stopImmediatePropagation();
        cls.EventHelper.preventCancelableDefault(event);
        try {
          const effects = event && event.dataTransfer && event.dataTransfer.effectAllowed;
          event.dataTransfer.dropEffect = "move" === effects || "linkMove" === effects ? "move" : "copy";
        } catch (e) {}
      },

      /**
       * Handler when mouse is over the drop zone
       * @param {Object} event - DOM event
       * @private
       */
      _onMouseOver: function (event) {
        this.addClasses("dropping");
      },

      /**
       * Handler when mouse leaves the drop zone
       * @param {Object} event - DOM event
       * @private
       */
      _onMouseOut: function (event) {
        this.removeClass("dropping");
      },

      /**
       * Handler when the file has changed
       * @private
       */
      _onFileChanged: function () {
        let result, i;

        //After dropping a file the dialog could still be open but the input widget no longer exist
        if (this._hiddenInput === null) {
          const application = context.SessionService.getCurrent().getCurrentApplication();
          const messageService = application.message;
          const userInterfaceNode = application.getNode(0);
          const userInterfaceWidget = userInterfaceNode.getController().getWidget();
          const msgWidget = userInterfaceWidget.getMessageWidget();
          if (this._allowMultipleFiles) {
            msgWidget.setText(gbc.I18NService.t("gwc.file.upload.files-already-uploaded"));
          } else {
            msgWidget.setText(gbc.I18NService.t("gwc.file.upload.file-already-uploaded"));
          }
          msgWidget.setMessageKind("error");

          messageService.addMessage("upload", msgWidget);
          messageService.handlePositions();
          return;
        }

        if (this._allowMultipleFiles) {
          result = [];
          if (this._files) {
            for (i = 0; i < this._files.length; i++) {
              if (this._files[i]) {
                result.push(this._files[i].name);
              }
            }
          }
          if (this._hiddenInput.files) {
            for (i = 0; i < this._hiddenInput.files.length; i++) {
              if (this._hiddenInput.files[i]) {
                result.push(this._hiddenInput.files[i].name);
              }
            }
          }
        } else {
          if (this._files && this._files[0] && this._files[0].name) {
            result = this._files[0].name;
          }
          if (this._hiddenInput.files && this._hiddenInput.files[0] && this._hiddenInput.files[0].name) {
            result = this._hiddenInput.files[0].name;
          }
        }
        if (result) {
          this.emit(cls.FileInputWidget.fileSelectionChangedEvent, result);
        }
      },

      /**
       * Set file extension to filter allowed files
       * @param {string} extension - allowed extension
       * @publicdoc
       */
      setExtension: function (extension) {
        if (extension !== ".*") {
          this._hiddenInput.setAttribute("accept", extension);
        }
      },

      /**
       * Set the caption for file upload
       * @param {string} caption - Caption to display
       * @publicdoc
       */
      setCaption: function (caption) {
        this._rawCaption = caption;
        this._updateCaption();
      },

      /**
       * Internal method to set the caption, or fallback on i18n one
       * @private
       */
      _updateCaption: function () {
        if (this._rawCaption) {
          this._element.querySelector("span").textContent = this._rawCaption;
        } else {
          this._element.querySelector("span").textContent = this._allowMultipleFiles
            ? gbc.I18NService.t("gwc.file.upload.droporclick-multiple")
            : gbc.I18NService.t("gwc.file.upload.droporclick");
        }
      },

      /**
       * Allows multiple files to be uploaded at once
       * @param {boolean} allow - true to allow multiple files, false otherwise
       * @publicdoc
       */
      allowMultipleFiles: function (allow) {
        this._allowMultipleFiles = Boolean(allow);
        if (allow) {
          this._hiddenInput.setAttribute("multiple", "multiple");
        } else {
          this._hiddenInput.removeAttribute("multiple");
        }
        this._updateCaption();
      },

      /**
       * Check if multiple files can be uploaded at once
       * @returns {boolean} true if allow multi-upload, false otherwise
       * @publicdoc
       */
      isAllowMultipleFiles: function () {
        return this._allowMultipleFiles;
      },

      /**
       * Event handler called when the files selection has changed
       * @param {function} hook - callback once event is triggered
       * @return {*|HandleRegistration}
       */
      whenFileSelectionChanged: function (hook) {
        return this.when(cls.FileInputWidget.fileSelectionChangedEvent, hook);
      },

      /**
       * Send files to the VM.
       * If you don't understand this method, please don't change it
       * @param {String} filename
       * @param {String} url
       * @param {function} callback - function called when done
       * @param {function} errorCallback - function called when error occurs
       * @param {function} progressHandler - function called when progress occurs
       */
      send: function (filename, url, callback, errorCallback, progressHandler) {
        filename = filename.normalize();
        let formData = null,
          i,
          file;
        if (this._files) {
          formData = new FormData();
          for (i = 0; i < this._files.length; ++i) {
            file = this._files[i];
            if (file.name.normalize() === filename) {
              formData.append(file.name.normalize(), file);
              break;
            }
            //TODO : manage multifile support
          }
        } else {
          formData = new FormData();
          const files = this._element.querySelector("form").file.files;
          for (i = 0; i < files.length; ++i) {
            file = files[i];
            if (file.name.normalize() === filename) {
              formData.append(file.name.normalize(), file);
              break;
            }
            //TODO : manage multifile support
          }
        }
        const request = new XMLHttpRequest();
        if (context.ThemeService.getValue("gbc-network-use-credentials-headers")) {
          request.withCredentials = true;
        }
        request.onload = function (event) {
          // HTTP 413 : the file is too big for the server: will be handled has error!
          if (event.target.status === 413) {
            errorCallback();
          } else {
            callback();
          }
        }.bind(this);
        request.onerror = function () {
          errorCallback();
        };
        request.open("POST", url);
        request.upload.addEventListener("progress", progressHandler.bind(this));
        request.send(formData);
      }
    };
  });
  cls.WidgetFactory.registerBuilder("FileInput", cls.FileInputWidget);
});
